import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  Doctor,
  DoctorUpdateRequest,
  Form,
  FormResponse,
  Investigation,
  Prescription,
  Metadata,
  UserLoginRequest,
  DoctorLoginRequest,
  FormResponseCreateRequest,
  PrescriptionUpdateRequest,
  ApiError,
  MetricsResponse,
  SchedulerSettings,
  AdminPaymentList,
  AdminPaymentListQuery,
  PaymentTransactions,
  AdminRefundResult
} from '../types/api';
import { logApiRequest, logApiResponse, logApiError } from '../utils/apiDebug';
import { cacheService } from './cacheService';
import { getApiBaseUrl } from './apiConfig';
import { sanitizeDetails } from '../utils/apiErrors';
import { clearAuthStorage } from '../utils/authStorage';

// API Configuration - Environment-aware
const API_BASE_URL = getApiBaseUrl();

// Polyfill for crypto.randomUUID() for older environments
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3)  | 0x8);
    return v.toString(16);
  });
};

// x-device-id must identify this browser installation stably across
// requests and reloads, so it's generated once and persisted rather than
// regenerated per request like x-correlation-id.
const DEVICE_ID_STORAGE_KEY = 'deviceId';

const getOrCreateDeviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const deviceId = generateUUID();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
};

const toPositiveInt = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
};

// A 401 from a login request itself (admin, or the unauthenticated
// user/doctor login endpoints this service also exposes) must NOT trigger
// the global re-login redirect: it means "wrong credentials", not "your
// session expired", and the calling page surfaces it inline. Matching any
// URL ending in /login (rather than hardcoding just /admin/login) keeps
// this correct if those other login calls are ever wired up to the UI.
// Any other 401 means an absent/expired admin session.
const LOGIN_ENDPOINT_PATTERN = /\/login(?:[?#]|$)/;

export const shouldForceReloginOn = (status: number | undefined, url: string | undefined): boolean =>
  status === 401 && !LOGIN_ENDPOINT_PATTERN.test(url || '');

// Concurrent in-flight requests can all reject with 401 at once (e.g. a
// dashboard firing several calls after the session already expired); this
// guard makes sure that only redirects the browser once instead of once per
// rejection.
let reauthRedirectInFlight = false;

// The actual navigation, behind a seam: jsdom's window.location.assign is
// non-configurable (can't be jest.spyOn'd) and replacing window.location
// wholesale breaks axios's XHR adapter, which needs it intact to resolve
// request URLs. Tests swap this in instead of touching window.location.
let navigateToLogin = (): void => {
  if (typeof window !== 'undefined') {
    window.location.assign('/login');
  }
};

// Exported so tests can reset the module-level guard between cases —
// production code never needs to call this (the real redirect navigates the
// page away, which clears the guard along with everything else).
export const resetReauthGuardForTests = (): void => {
  reauthRedirectInFlight = false;
};

export const setNavigateToLoginForTests = (fn: () => void): void => {
  navigateToLogin = fn;
};

const triggerReauthRedirect = (): void => {
  if (reauthRedirectInFlight) return;
  reauthRedirectInFlight = true;
  try {
    clearAuthStorage();
  } finally {
    navigateToLogin();
  }
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add Authorization header if stored
        const storedAuthHeader = localStorage.getItem('authHeader');
        if (storedAuthHeader) {
          config.headers.Authorization = storedAuthHeader;
        } else {
          const token = localStorage.getItem('authToken');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }


        // Keep Authorization if set above; do not override with a placeholder

        // x-device-id identifies this installation and stays stable across
        // requests/reloads; x-correlation-id is fresh per request. Both are
        // RFC4122 v4 UUIDs, e.g. /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
        config.headers['x-device-id'] = getOrCreateDeviceId();
        config.headers['x-correlation-id'] = generateUUID();
        
        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          logApiRequest(config);
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          logApiResponse(response);
        }
        return response;
      },
      (error) => {
        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
          logApiError(error);
        }

        const status: number = error.response?.status || 500;
        const data = error.response?.data;

        if (shouldForceReloginOn(status, error.config?.url)) {
          triggerReauthRedirect();
        }

        const apiError: ApiError = {
          message: data?.message || 'An error occurred',
          status,
        };

        const details = sanitizeDetails(data?.details);
        if (details.length > 0) {
          apiError.details = details;
        }

        if (status === 423) {
          const retryAfter = toPositiveInt(data?.retryAfter);
          if (retryAfter != null) {
            apiError.retryAfter = retryAfter;
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  // User APIs
  async getAllUsers(): Promise<User[]> {
    // Check cache first
    const cached = cacheService.get<User[]>('users');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<User[]> = await this.api.get('/users/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('users', data);
    
    return data;
  }

  async getUserById(uid: string): Promise<User> {
    // Check cache first
    const cached = cacheService.get<User>('users', uid);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<User> = await this.api.post(`/users/sb/${uid}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('users', data, uid);
    
    return data;
  }

  async loginUser(request: UserLoginRequest): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/users/login', request);
    return response.data;
  }

  // Admin login - captures Authorization header and stores it
  async adminLogin(params: { email: string; password: string }): Promise<any> {
    const response = await this.api.post('/admin/login', params);
    const headerValue = (response.headers && (response.headers['authorization'] || (response.headers as any)['Authorization'])) as string | undefined;
    if (headerValue) {
      localStorage.setItem('authHeader', headerValue);
      // For backward compatibility with existing token-based logic
      const token = headerValue.startsWith('Bearer ') ? headerValue.slice(7) : headerValue;
      localStorage.setItem('authToken', token);
    }
    return response.data;
  }

  async signupUser(request: User): Promise<{ newUser: User }> {
    const response: AxiosResponse<{ newUser: User }> = await this.api.post('/users/signup', request);
    const data = response.data;
    
    // Cache the new user and invalidate list cache
    cacheService.set('users', data.newUser, data.newUser.sbu_id);
    cacheService.invalidateOnUpdate('users');
    
    return data;
  }

  async blockUser(uid: string): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post(`/users/${uid}/block`);
    const data = response.data;
    
    // Update cache with new user data
    cacheService.set('users', data, uid);
    cacheService.invalidateOnUpdate('users');
    
    return data;
  }

  async deleteUser(uid: string): Promise<User> {
    const response: AxiosResponse<User> = await this.api.delete(`/users/${uid}`);
    const data = response.data;
    
    // Remove from cache and invalidate list cache
    cacheService.invalidateOnDelete('users', uid);
    
    return data;
  }

  async verifyUser(uid: string): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/users/verify', { uid });
    const data = response.data;
    
    // Update cache with new user data
    cacheService.set('users', data, uid);
    cacheService.invalidateOnUpdate('users');
    
    return data;
  }

  async getUserBySbuId(sbu_id: string): Promise<User> {
    // Check cache first
    const cached = cacheService.get<User>('users', sbu_id);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<User> = await this.api.post(`/sb/${sbu_id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('users', data, sbu_id);
    
    return data;
  }

  async createUser(user: User): Promise<{ newUser: User }> {
    const response: AxiosResponse<{ newUser: User }> = await this.api.post('users/signup', user);
    const data = response.data;
    
    // Cache the new user and invalidate list cache
    cacheService.set('users', data.newUser, data.newUser.sbu_id);
    cacheService.invalidateOnUpdate('users');
    
    return data;
  }

  async updateUser(uid: string, user: User): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${uid}`, user);
    const data = response.data;
    
    // Update cache with new user data
    cacheService.set('users', data, uid);
    cacheService.invalidateOnUpdate('users');
    
    return data;
  }

  // Doctor APIs
  async getAllDoctors(): Promise<Doctor[]> {
    // Check cache first
    const cached = cacheService.get<Doctor[]>('doctors');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Doctor[]> = await this.api.get('/doctors/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('doctors', data);
    
    return data;
  }

  async getDoctorById(sbd_id: string): Promise<Doctor> {
    // Check cache first
    const cached = cacheService.get<Doctor>('doctors', sbd_id);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Doctor> = await this.api.get(`/doctors/${sbd_id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('doctors', data, sbd_id);
    
    return data;
  }

  async createDoctor(doctor: Doctor): Promise<{ doctor: Doctor }> {
    const response: AxiosResponse<{ doctor: Doctor }> = await this.api.post('/doctors/new', doctor);
    const data = response.data;
    
    // Cache the new doctor and invalidate list cache
    cacheService.set('doctors', data.doctor, data.doctor.sbd_id);
    cacheService.invalidateOnUpdate('doctors');
    
    return data;
  }

  async updateDoctor(sbd_id: string, doctor: DoctorUpdateRequest): Promise<Doctor> {
    const response: AxiosResponse<Doctor> = await this.api.put(`/doctors/update/${sbd_id}`, doctor);
    const data = response.data;
    
    // Update cache with new doctor data
    cacheService.set('doctors', data, sbd_id);
    cacheService.invalidateOnUpdate('doctors');
    
    return data;
  }

  async deleteDoctor(sbd_id: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/doctors/delete/${sbd_id}`);
    const data = response.data;
    
    // Remove from cache and invalidate list cache
    cacheService.invalidateOnDelete('doctors', sbd_id);
    
    return data;
  }

  async blockDoctor(sbd_id: string): Promise<Doctor> {
    const response: AxiosResponse<Doctor> = await this.api.post(`/doctors/${sbd_id}/block`);
    const data = response.data;
    
    // Update cache with new doctor data
    cacheService.set('doctors', data, sbd_id);
    cacheService.invalidateOnUpdate('doctors');
    
    return data;
  }

  async loginDoctor(request: DoctorLoginRequest): Promise<Doctor> {
    const response: AxiosResponse<Doctor> = await this.api.post('/doctors/login', request);
    return response.data;
  }

  async logoutDoctor(request: { email: string }): Promise<Doctor> {
    const response: AxiosResponse<Doctor> = await this.api.post('/doctors/logout', request);
    return response.data;
  }

  async resetDoctorPassword(sbd_id: string): Promise<{ doctor: Doctor; newPassword: string }> {
    const response: AxiosResponse<{ doctor: Doctor; newPassword: string }> = await this.api.post(`/doctors/${sbd_id}/reset-password`);
    const data = response.data;
    
    // Update cache with new doctor data
    cacheService.set('doctors', data.doctor, sbd_id);
    cacheService.invalidateOnUpdate('doctors');
    
    return data;
  }

  // Form APIs
  async getAllForms(): Promise<Form[]> {
    // Check cache first
    const cached = cacheService.get<Form[]>('forms');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Form[]> = await this.api.get('/forms/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('forms', data);
    
    return data;
  }

  async getFormById(id: string): Promise<Form> {
    // Check cache first
    const cached = cacheService.get<Form>('forms', id);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Form> = await this.api.get(`/forms/form/${id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('forms', data, id);
    
    return data;
  }

  async createForm(form: Form): Promise<Form> {
    const response: AxiosResponse<Form> = await this.api.post('/forms/new', form);
    const data = response.data;
    
    // Cache the new form and invalidate list cache
    if (data._id) {
      cacheService.set('forms', data, data._id);
    }
    cacheService.invalidateOnUpdate('forms');
    
    return data;
  }

  async updateForm(id: string, form: Form): Promise<void> {
    await this.api.put(`/forms/form/${id}`, form);
    
    // Update cache with new form data
    cacheService.set('forms', form, id);
    cacheService.invalidateOnUpdate('forms');
  }

  async deleteForm(id: string): Promise<void> {
    await this.api.delete(`/forms/form/${id}`);
    
    // Remove from cache and invalidate list cache
    cacheService.invalidateOnDelete('forms', id);
  }

  // Form Response APIs
  async getAllFormResponses(): Promise<FormResponse[]> {
    // Check cache first
    const cached = cacheService.get<FormResponse[]>('formResponses');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<FormResponse[]> = await this.api.get('/form-responses/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('formResponses', data);
    
    return data;
  }

  async getFormResponseByFormId(id: string): Promise<FormResponse> {
    // Check cache first
    const cached = cacheService.get<FormResponse>('formResponses', `form_${id}`);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<FormResponse> = await this.api.get(`/form-responses/form/${id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('formResponses', data, `form_${id}`);
    
    return data;
  }

  async getFormResponsesBySbuId(id: string): Promise<FormResponse[]> {
    // Check cache first
    const cached = cacheService.get<FormResponse[]>('formResponses', `sbu_${id}`);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<FormResponse[]> = await this.api.get(`/form-responses/sbu/${id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('formResponses', data, `sbu_${id}`);
    
    return data;
  }

  async createFormResponse(request: FormResponseCreateRequest): Promise<FormResponse> {
    const response: AxiosResponse<FormResponse> = await this.api.post('/form-responses/new', request);
    const data = response.data;
    
    // Cache the new form response and invalidate list cache
    if (data._id) {
      cacheService.set('formResponses', data, data._id);
    }
    cacheService.invalidateOnUpdate('formResponses');
    
    return data;
  }

  // Investigation APIs
  async getAllInvestigations(): Promise<Investigation[]> {
    // Check cache first
    const cached = cacheService.get<Investigation[]>('investigations');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Investigation[]> = await this.api.get('/investigations/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('investigations', data);
    
    return data;
  }

  async createInvestigation(investigation: Investigation): Promise<Investigation> {
    const response: AxiosResponse<Investigation> = await this.api.post('/investigations/new', investigation);
    const data = response.data;
    
    // Cache the new investigation and invalidate list cache
    if (data.iv_id) {
      cacheService.set('investigations', data, data.iv_id);
    }
    cacheService.invalidateOnUpdate('investigations');
    
    return data;
  }

  // Prescription APIs
  async getAllPrescriptions(): Promise<Prescription[]> {
    // Check cache first
    const cached = cacheService.get<Prescription[]>('prescriptions');
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Prescription[]> = await this.api.get('/prescriptions/all');
    const data = response.data;
    
    // Cache the result
    cacheService.set('prescriptions', data);
    
    return data;
  }

  async getPrescriptionById(id: string): Promise<Prescription> {
    // Check cache first
    const cached = cacheService.get<Prescription>('prescriptions', id);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Prescription> = await this.api.get(`/prescriptions/prescription/${id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('prescriptions', data, id);
    
    return data;
  }

  async getPrescriptionByUserId(sbu_id: string): Promise<Prescription> {
    // Check cache first
    const cached = cacheService.get<Prescription>('prescriptions', `user_${sbu_id}`);
    if (cached) {
      return cached;
    }

    // Fetch from API if not cached
    const response: AxiosResponse<Prescription> = await this.api.get(`/prescriptions/user/${sbu_id}`);
    const data = response.data;
    
    // Cache the result
    cacheService.set('prescriptions', data, `user_${sbu_id}`);
    
    return data;
  }

  async getPrescriptionsByDoctorId(sbd_id: string): Promise<Prescription[]> {
    const cached = cacheService.get<Prescription[]>('prescriptions', `doctor_${sbd_id}`);
    if (cached) {
      return cached;
    }

    const response: AxiosResponse<Prescription[]> = await this.api.get(`/prescriptions/doctor/${sbd_id}`);
    const data = response.data;
    
    cacheService.set('prescriptions', data, `doctor_${sbd_id}`);
    
    return data;
  }

  async createPrescription(prescription: Prescription): Promise<Prescription> {
    const response: AxiosResponse<Prescription> = await this.api.post('/prescriptions/new', prescription);
    const data = response.data;
    
    // Cache the new prescription and invalidate list cache
    if (data.prescription_id) {
      cacheService.set('prescriptions', data, data.prescription_id);
    }
    cacheService.invalidateOnUpdate('prescriptions');
    
    return data;
  }

  async updatePrescription(id: string, request: PrescriptionUpdateRequest): Promise<Prescription> {
    const response: AxiosResponse<Prescription> = await this.api.put(`/prescriptions/prescription/${id}`, request);
    const data = response.data;
    
    // Update cache with new prescription data
    cacheService.set('prescriptions', data, id);
    cacheService.invalidateOnUpdate('prescriptions');
    
    return data;
  }

  async deletePrescription(id: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/prescriptions/prescription/${id}`);
    const data = response.data;
    
    // Remove from cache and invalidate list cache
    cacheService.invalidateOnDelete('prescriptions', id);
    
    return data;
  }

  // Metadata APIs
  async createMetadata(key: string, data: any): Promise<Metadata> {
    const response: AxiosResponse<Metadata> = await this.api.post('/metadata', { key, data });
    const metadata = response.data;

    cacheService.set('metadata', metadata, key);
    cacheService.invalidateOnUpdate('metadata');

    return metadata;
  }

  async getMetadataByKey(key: string): Promise<Metadata> {
    const cached = cacheService.get<Metadata>('metadata', key);
    if (cached) {
      return cached;
    }

    const response: AxiosResponse<Metadata> = await this.api.get(`/metadata/${encodeURIComponent(key)}`);
    const metadata = response.data;

    cacheService.set('metadata', metadata, key);

    return metadata;
  }

  async updateMetadata(key: string, data: any): Promise<Metadata> {
    const response: AxiosResponse<Metadata> = await this.api.put(`/metadata/${encodeURIComponent(key)}`, { data });
    const metadata = response.data;

    cacheService.set('metadata', metadata, key);
    cacheService.invalidateOnUpdate('metadata');

    return metadata;
  }

  async deleteMetadata(key: string): Promise<void> {
    await this.api.delete(`/metadata/${encodeURIComponent(key)}`);
    cacheService.invalidateOnDelete('metadata', key);
  }

  async getAllMetadata(): Promise<Metadata[]> {
    const cached = cacheService.get<Metadata[]>('metadata');
    if (cached) {
      return cached;
    }

    const response: AxiosResponse<Metadata[]> = await this.api.get('/metadata');
    const data = response.data;

    cacheService.set('metadata', data);

    return data;
  }

  // Metrics APIs
  async getMetrics(): Promise<MetricsResponse> {
    const response: AxiosResponse<MetricsResponse> = await this.api.get('/admin/metrics');
    return response.data;
  }

  async resetMetrics(): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/admin/metrics/reset');
    return response.data;
  }

  // Scheduler settings APIs. Deliberately uncached: this is a single global
  // value an admin edits here, so every read should reflect the backend.
  async getSchedulerSettings(): Promise<SchedulerSettings> {
    const response: AxiosResponse<SchedulerSettings> = await this.api.get('/admin/scheduler-settings');
    return response.data;
  }

  async updateSchedulerSettings(settings: SchedulerSettings): Promise<SchedulerSettings> {
    const response: AxiosResponse<SchedulerSettings> = await this.api.put('/admin/scheduler-settings', settings);
    return response.data;
  }

  // Admin payment APIs (sb-express-app specs 34-35). Uncached: refund state
  // moves on webhooks and retries, so every read should reflect the backend.
  async listAdminPayments(query: AdminPaymentListQuery): Promise<AdminPaymentList> {
    const { needs_attention, ...rest } = query;
    // The backend accepts only needs_attention=true; its complement is "omit it".
    const params = needs_attention ? { ...rest, needs_attention: 'true' } : rest;
    const response: AxiosResponse<AdminPaymentList> = await this.api.get('/admin/payments', { params });
    return response.data;
  }

  async getPaymentTransactions(paymentId: string): Promise<PaymentTransactions> {
    const response: AxiosResponse<PaymentTransactions> = await this.api.get(
      `/admin/payments/${encodeURIComponent(paymentId)}/transactions`
    );
    return response.data;
  }

  // A full refund, or a retry of a failed or stuck one, on exactly one refund
  // target. The target is always named, even when the Payment has only one,
  // so a call can never act on a target the admin didn't choose.
  async refundPayment(paymentId: string, razorpayPaymentId: string): Promise<AdminRefundResult> {
    const response: AxiosResponse<AdminRefundResult> = await this.api.post(
      `/admin/payments/${encodeURIComponent(paymentId)}/refund`,
      { razorpay_payment_id: razorpayPaymentId }
    );
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService; 